/**
 * Web Push Notifications via Firebase Cloud Messaging
 *
 * Handles FCM token lifecycle for PWA users:
 * - Permission request
 * - Token registration with backend
 * - Token refresh
 * - Foreground message handling
 * - Location updates and city subscription sync (settings live on /volg)
 */

import {
    FIREBASE_CONFIG, VAPID_KEY, STORAGE_KEYS,
} from './config.js';
import { prefsBody } from './push-prefs.js';

// Shared with the settings panel (volg-settings.js); app.js imports it from here.
export { updatePushLocation } from './push-prefs.js';
import { userLocation, loadManualLocation } from './state.js';
import {
    getFollowedCities, setFollowedCities, isCityFollowed,
    subscribeCity, unsubscribeCity,
    syncCitySubscriptions as _syncCitySubscriptions,
} from './push-cities.js';

let messaging = null;

/**
 * Check if web push is available in this environment
 */
export function isWebPushAvailable() {
    if (!('serviceWorker' in navigator)) return false;
    if (!('PushManager' in window)) return false;
    if (typeof firebase === 'undefined') return false;
    return true;
}

/**
 * Get current push permission state
 * Returns 'granted', 'denied', 'default', or 'unavailable'
 */
export function getWebPushState() {
    if (!isWebPushAvailable()) return 'unavailable';
    if (localStorage.getItem(STORAGE_KEYS.webPushToken)) return 'granted';
    return Notification.permission;
}

// --- Followed cities (shared impl in push-cities.js) ---

export { getFollowedCities, setFollowedCities, isCityFollowed };

export async function subscribeToCityPush(slug) {
    const token = localStorage.getItem(STORAGE_KEYS.webPushToken);
    if (!token) return false;
    return subscribeCity(token, slug);
}

export async function unsubscribeFromCityPush(slug) {
    const token = localStorage.getItem(STORAGE_KEYS.webPushToken);
    if (!token) return false;
    return unsubscribeCity(token, slug);
}

export async function syncCitySubscriptions() {
    const token = localStorage.getItem(STORAGE_KEYS.webPushToken);
    if (!token) return;
    return _syncCitySubscriptions(token);
}

/**
 * Initialize Firebase Messaging (call after SW registration)
 * If permission already granted, silently refreshes the token.
 */
export async function initWebPush() {
    if (!isWebPushAvailable()) return;

    try {
        const app = firebase.initializeApp(FIREBASE_CONFIG);
        messaging = firebase.messaging();

        // If already granted, silently refresh or re-register token
        if (Notification.permission === 'granted') {
            if (localStorage.getItem(STORAGE_KEYS.webPushToken)) {
                await refreshToken();
            } else {
                // Token was lost (e.g. cleared localStorage) but permission still granted
                // Silently re-register — no prompt will appear
                console.log('[WebPush] Permission granted but no token, re-registering');
                await requestWebPush();
            }
        }
    } catch (err) {
        console.error('[WebPush] Init failed:', err);
    }
}

/**
 * Request push permission and register token with backend
 * Returns true if successfully subscribed
 */
export async function requestWebPush() {
    if (!messaging) return false;

    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.log('[WebPush] Permission denied');
            return false;
        }

        // Wait for service worker to be ready
        const registration = await navigator.serviceWorker.ready;

        const token = await messaging.getToken({
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration,
        });

        if (!token) {
            console.error('[WebPush] No token received');
            return false;
        }

        console.log('[WebPush] Token received');

        // Register with backend
        const registered = await registerTokenWithBackend(token);
        if (registered) {
            localStorage.setItem(STORAGE_KEYS.webPushToken, token);
        }
        return registered;
    } catch (err) {
        console.error('[WebPush] Request failed:', err);
        return false;
    }
}

/**
 * Refresh token if it changed (e.g. after browser update)
 */
async function refreshToken() {
    if (!messaging) return;

    try {
        const registration = await navigator.serviceWorker.ready;
        const newToken = await messaging.getToken({
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration,
        });

        const oldToken = localStorage.getItem(STORAGE_KEYS.webPushToken);
        if (newToken && newToken !== oldToken) {
            console.log('[WebPush] Token refreshed, re-registering');
            const registered = await registerTokenWithBackend(newToken);
            if (registered) {
                localStorage.setItem(STORAGE_KEYS.webPushToken, newToken);
            }
        }
    } catch (err) {
        console.error('[WebPush] Token refresh failed:', err);
    }
}

/**
 * Register FCM token with our backend
 */
async function registerTokenWithBackend(token) {
    // Use live geolocation if available, otherwise fall back to manual override.
    let lat = null;
    let lon = null;
    if (userLocation && userLocation.lat && userLocation.lon) {
        lat = userLocation.lat;
        lon = userLocation.lon;
    } else {
        const manual = loadManualLocation();
        if (manual && manual.lat && manual.lon) {
            lat = manual.lat;
            lon = manual.lon;
        }
    }

    try {
        const response = await fetch('/api/push/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, lat, lon, platform: 'web', ...prefsBody() }),
        });

        if (response.ok) {
            console.log('[WebPush] Token registered with backend');
            if (lat !== null && lon !== null) {
                localStorage.setItem(STORAGE_KEYS.pushLocationSent, JSON.stringify({ lat, lon }));
            }
            return true;
        }
        console.error('[WebPush] Backend rejected registration:', response.status);
    } catch (err) {
        console.error('[WebPush] Backend registration failed:', err);
    }
    return false;
}
